import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './OrderDetailsGrid.css';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import eFormType from '../../../main/assets/enums/eFormType';
import DeleteModal from '../../../main/components/modal/delete/DeleteModal';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import IOrder from '../../../main/interfaces/IOrder';
import OrderInventory from '../../restaurant/order/orderInventory/OrderInventory';
import axiosInstance from '../../../main/initializers/axios';
import IProduct from '../../../main/interfaces/IProduct';
import EditModal from '../../../main/components/modal/edit/EditModal';
import IGridItems from '../../../main/interfaces/IGridItems';
import IPagination from '../../../main/interfaces/IPagination';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';

type OrderStatusFilter = 'all' | 'active' | 'completed';

const OrderDetailsGrid = () => {
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState<boolean>(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [approved, setApproved] = useState<boolean>(false);
  const [selectedVal, setSelectedVal] = useState<IOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('all');
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const { i18n } = useTranslation();

  const getProducts = useCallback(() => {
    axiosInstance.get(ApiConstants.Products.list).then((res) => {
      setProducts(res.data ?? []);
    });
  }, []);

  const getTable = useCallback(
    (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];

      axiosInstance.post(ApiConstants.order.listct, { gridItems: effectiveGrid, pagination: effective }).then((res) => {
        const items = res.data?.items ?? [];
        const p = res.data?.pagination ?? {};

        setOrders(items);
        setGridItems(res.data?.gridItems ?? []);

        setPagination((prev) => ({
          ...prev,
          count: p.count ?? prev.count,
          page: p.page ?? prev.page,
          itemPerPage: p.itemPerPage ?? prev.itemPerPage,
          itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
        }));
      });
    },
    [gridItems, pagination],
  );

  const selectedItemPagination = (pag: IPagination) => {
    setPagination(pag);
    getTable(pag);
  };

  const deleteDataSet = (id: number) => {
    axiosInstance.post(ApiConstants.order.delete, { Id: id }).then(() => {
      setIsModalDeleteOpen(false);
      setOrders((prev) => prev.filter((x) => x.id !== id));
    });
  };

  const setOrderOnComplete = (id: number) => {
    axiosInstance.post(ApiConstants.order.complete, { Id: id }).then(() => {
      getTable();
    });
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const statusDesc = (o.statusDesc ?? '').toLowerCase();
      return (
        statusFilter === 'all' ||
        (statusFilter === 'active' && statusDesc === 'active') ||
        (statusFilter === 'completed' && (statusDesc === 'complete' || statusDesc === 'completed'))
      );
    });
  }, [orders, statusFilter]);

  const stats = useMemo(() => {
    const total = orders.length;
    const active = orders.filter((o) => (o.statusDesc ?? '').toLowerCase() === 'active').length;
    const completed = orders.filter((o) => {
      const s = (o.statusDesc ?? '').toLowerCase();
      return s === 'complete' || s === 'completed';
    }).length;

    return { total, active, completed };
  }, [orders]);

  const hasActiveFilters = statusFilter !== 'all';

  const handleEdit = (item: IOrder) => {
    setSelectedVal(item);
    setIsModalEditOpen(true);
  };

  const handleDelete = (item: IOrder) => {
    setSelectedVal(item);
    setIsModalDeleteOpen(true);
  };

  const clearFilters = () => {
    setStatusFilter('all');
  };

  useEffect(() => {
    getTable();
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BasicCli>
      <div className="odWrap bcPageContent">
        <div className="odContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('orderDetails.supplyOrder')}</h1>
            <div className="bcPageHeaderActions">
              <div className="odField">
                <FilterListIcon className="odFieldIcon" />
                <select className="ui-control ui-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as OrderStatusFilter)}>
                  <option value="all">{i18n.t('orderDetails.allOrders')}</option>
                  <option value="active">{i18n.t('orderDetails.active')}</option>
                  <option value="completed">{i18n.t('orderDetails.completed')}</option>
                </select>
              </div>

              <button className="ui-control ui-btn ui-btn--primary odAddBtn" onClick={() => setIsModalOpen(true)}>
                <AddIcon />
                <span>{i18n.t('orderDetails.addNewOrder')}</span>
              </button>
            </div>
          </div>

          <div className="odCards">
            <div className="bcCard odCard">
              <div className="odIconWrap">
                <ShoppingCartIcon fontSize="large" className="odIcon" />
              </div>
              <h3 className="odCardTitle">{i18n.t('orderDetails.totalOrders')}</h3>
              <p className="odValue">{stats.total}</p>
              <div className="odSub">
                <span className="odBadge odBadge--primary">
                  {filteredOrders.length} {i18n.t('orderDetails.filtered')}
                </span>
              </div>
            </div>

            <div className="bcCard odCard">
              <div className="odIconWrap">
                <AccessTimeIcon fontSize="large" className="odIcon" />
              </div>
              <h3 className="odCardTitle">{i18n.t('orderDetails.activeOrders')}</h3>
              <p className="odValue">{stats.active}</p>
              <div className="odSub">
                <span className="odBadge odBadge--warning">{i18n.t('orderDetails.waitingForCompletion')}</span>
              </div>
            </div>

            <div className="bcCard odCard">
              <div className="odIconWrap">
                <CheckCircleIcon fontSize="large" className="odIcon" />
              </div>
              <h3 className="odCardTitle">{i18n.t('orderDetails.completed')}</h3>
              <p className="odValue">{stats.completed}</p>
              <div className="odSub">
                <span className="odBadge odBadge--success">{i18n.t('orderDetails.successfullyDelivered')}</span>
              </div>
            </div>
          </div>

          {hasActiveFilters ? (
            <div className="ui-panel odFilters">
              <div className="odFilterStatus is-visible">
                <span className="bcMuted">
                  Showing <strong className="bcText">{filteredOrders.length}</strong> of <strong className="bcText">{stats.total}</strong> orders
                </span>

                <button className="ui-control ui-btn odClearBtn" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            </div>
          ) : null}

          <div className=" odTable">
            <GeneralTable
              data={filteredOrders}
              columns={gridItems}
              gridEntity={7}
              title=""
              hasRp={true}
              selectedItem={(val: any) => setSelectedVal(val)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              pagination={pagination}
              selectedItemPagination={(val: any) => selectedItemPagination(val)}
              isCompleteShown={true}
              setComplete={(val) => setOrderOnComplete(val)}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            />
          </div>

          {isModalEditOpen && (
            <EditModal
              title={i18n.t('orderDetails.editOrder')}
              width="80%"
              closeModal={() => setIsModalEditOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalEditOpen}
              modalType={eFormType.Edit}
            >
              <OrderInventory
                products={products}
                createOrUpdate={approved}
                tempProduct={selectedVal}
                modalType={eFormType.Edit}
                onSubmitData={() => {
                  setIsModalEditOpen(false);
                  setApproved(false);
                  getTable();
                }}
              />
            </EditModal>
          )}

          {isModalDeleteOpen && (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal?.id ?? 0)}>
              {i18n.t('orderDetails.areYouSureYouWantToDeleteOrder')} <strong className="text-danger"> #{selectedVal?.id}</strong>?
            </DeleteModal>
          )}

          {isModalOpen && (
            <EditModal
              title={i18n.t('orderDetails.addNewOrder')}
              width="80%"
              closeModal={() => setIsModalOpen(false)}
              onSubmitData={() => setApproved(true)}
              setIsModalOpen={setIsModalOpen}
              modalType={eFormType.New}
            >
              <OrderInventory
                createOrUpdate={approved}
                products={products}
                modalType={eFormType.New}
                onSubmitData={() => {
                  setApproved(false);
                  getTable();
                  setIsModalOpen(false);
                }}
              />
            </EditModal>
          )}
        </div>
      </div>
    </BasicCli>
  );
};

export default OrderDetailsGrid;
